import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, CardActions, Dialog, IconButton, TextField, InputAdornment, Avatar, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, useMediaQuery, Chip } from '@mui/material';
import { Add, Edit, Close, Phone } from '@mui/icons-material';
import AdminCreation from '../SuperAdmin/AdminCreation';
import toast from 'react-hot-toast';
import { deleteAdminAction, getAllAdminsAction, postCreateAdminAction, updateAdminAction } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

function SuperAdminUserManagement() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const { successData } = useSelector(state => state.postCreateAdminReducer);


  // Form state for editing user
  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
  });

  useEffect(() => {
    if (phoneSearch.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.phoneNumber.includes(phoneSearch)
      );
      setFilteredUsers(filtered);
    }
  }, [phoneSearch, users]);

  const handleAddUser = () => {
    setEditingUser(null);
    setOpenAddDialog(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      fullName: user.fullName,
      email: user.email,
      phoneNumber: user.phoneNumber,
    });
    setOpenEditDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingUser(null);
  };

  const handleFormSubmit = (userData) => {
    if (editingUser) {
      const updatedUsers = users.map(user => user.id === editingUser.id ? { ...user, ...userData } : user);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      toast.success('User updated successfully');
    } else {
      const newUser = {
        id: users.length + 1,
        ...userData
      };
      const updatedUsers = [...users, newUser];
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      toast.success('User added successfully');
    }
    handleCloseAddDialog();
    handleCloseEditDialog();
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditFormSubmit = () => {
    console.log(editForm);
    console.log(editingUser.id);
    dispatch(updateAdminAction({

      endPoint: `super-admin/admin/${editingUser.id}`,

      full_name: editForm.fullName,
      email: editForm.email,
      mobile_no: editForm.phoneNumber,

    }));
    handleCloseEditDialog();
  };

  const handleRemoveUser = (userId) => {
    setUserToDelete(userId);
    setConfirmDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setRemovingUserId(userToDelete);
    try {
      const response = await dispatch(deleteAdminAction({
        endPoint: `super-admin/admin/${userToDelete}`,
      })).unwrap();

      if (response.success) {
        const updatedUsers = users.filter(user => user.id !== userToDelete);
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
        toast.success('User removed successfully');
        dispatch(getAllAdminsAction());
      } else {
        toast.error('Failed to remove user');
      }
    } catch (error) {
      toast.error('An error occurred while removing the user');
    } finally {
      setRemovingUserId(null);
      setConfirmDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setConfirmDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleSaveChangesClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmSave = () => {
    setOpenConfirmDialog(false);
    handleEditFormSubmit();
  };

  const handleCancelSave = () => {
    setOpenConfirmDialog(false);
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Function to determine chip color based on subscription status
  const getSubscriptionChipColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    const fetchAdmins = async () => {
      setLoading(true);
      try {
        const response = await dispatch(getAllAdminsAction()).unwrap();
        console.log("responseee", response);
        if (response.success) {
          const admins = response.data.map(admin => ({
            id: admin.user_id,
            fullName: admin.full_name,
            email: admin.email,
            phoneNumber: admin.mobile_no,
            subscriptionStatus: admin.Client?.subscription_status,
          }));
          setUsers(admins);
          setFilteredUsers(admins);
        } else {
          toast.error('Failed to fetch admins');
        }
      } catch (error) {
        toast.error('An error occurred while fetching admins');
        console.log("Error fetching admins:", error.message);
        
      } finally {
        setLoading(false);
      }
    };

    fetchAdmins();
  }, [dispatch]);

  useEffect(() => {
    if (successData) {
      window.location.reload();
    }
  }, [successData]);

  return (
    <Box sx={{ p: 3, mt: { xs: 12, sm: 6, md: 15 }, width: '75%', ml: { xs: '7%', sm: '20%' } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'space-between' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          mb: 3,
        }}>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, marginBottom: { xs: 2, sm: 0 } }}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddUser}
          sx={{
            borderRadius: '8px',
            padding: { xs: '8px 16px', sm: '10px 20px' },
          }}
        >
          Add New User
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by phone number..."
          variant="outlined"
          size="small"
          value={phoneSearch}
          onChange={(e) => setPhoneSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone />
              </InputAdornment>
            ),
            endAdornment: phoneSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setPhoneSearch('')}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: '8px', width: { xs: '100%', sm: '50%', md: '30%' }, }
          }}
        />
      </Box>

      {loading ? (
        <Typography align="center">Loading...</Typography>
      ) : filteredUsers.length === 0 ? (
        <Typography align="center">No users found</Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 3,
                borderRadius: '12px',
                transition: 'transform 0.2s',
                position: 'relative',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-5px)' },
                  boxShadow: 6,
                }
              }}>
                {/* Subscription Status Badge */}
                <Chip
                  label={user.subscriptionStatus }
                  color={getSubscriptionChipColor(user.subscriptionStatus)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 1,
                    fontWeight: 'bold',
                  }}
                />

                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.main',
                        mb: 1
                      }}
                    >
                      {getInitials(user.fullName)}
                    </Avatar>
                    <Typography variant="h6" component="div" align="center">
                      {user.fullName}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <strong>Email:</strong>&nbsp;{user.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <strong>Phone:</strong>&nbsp;{user.phoneNumber}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEditUser(user)}
                    size="small"
                  >
                    Edit User
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleRemoveUser(user.id)}
                    size="small"
                    sx={{ ml: 1 }}
                  >Remove
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add User Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        fullScreen={useMediaQuery(theme.breakpoints.down('sm'))}
      >
        <AdminCreation
          onClose={handleCloseAddDialog}
          onSubmitSuccess={handleFormSubmit}
        />
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit User
          <IconButton
            aria-label="close"
            onClick={handleCloseEditDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Full Name"
              name="fullName"
              value={editForm.fullName}
              onChange={handleEditFormChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={editForm.email}
              onChange={handleEditFormChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              value={editForm.phoneNumber}
              onChange={handleEditFormChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveChangesClick}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelSave}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to save the changes?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave}>No</Button>
          <Button
            variant="contained"
            onClick={handleConfirmSave}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={cancelDeleteUser}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteUser}>No</Button>
          <Button
            variant="contained"
            onClick={confirmDeleteUser}
            color="error"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SuperAdminUserManagement;

